import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, of, Subject } from 'rxjs';
import { first, map, take, takeUntil } from 'rxjs/operators';
import { ApplicationService, SchoolInfo, SchoolNominationInfo, SchoolNomineeInfo } from 'services/application/application.service';
import { AwardsService, AwardProgramInfo } from 'services/awards/awards.service';
import { ErrorService } from 'services/error/error.service';
import { SettingsService } from 'services/settings/settings.service';
import { UserService, UserInfo } from 'services/user/user.service';

@Component({
    selector: 'app-attachments',
    styleUrls: ['attachments.component.scss'],
    templateUrl: 'attachments.component.html',
})

export class AttachmentsComponent implements OnDestroy, OnInit {
    private _subrelease: Subject<void> = new Subject<void>();
    AwardInfo: AwardProgramInfo;
    CurrentSchool: SchoolInfo;
    CurrentSchoolYear: string;
    Nominations: SchoolNomineeInfo[];
    User: UserInfo;
    ViewReady: boolean;

    constructor(
        private appSvc: ApplicationService,
        private awardSvc: AwardsService,
        private errSvc: ErrorService,
        private settingsSvc: SettingsService,
        private userSvc: UserService
    ) {}

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.AwardInfo = null;
        self.CurrentSchool = null;
        self.CurrentSchoolYear = null;
        self.Nominations = null;
        self.User = null;
        self.ViewReady = false;

        // Look for first non-null user object
        self.userSvc.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(_u => !!_u && !!_u.AuthToken)
        ).pipe(takeUntil(self._subrelease)).subscribe(_user => {
			self.User = _user;
			if (_user == null) return;

            // Set up constant user monitoring
            self.userSvc.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(user => (self.User = user));

            // Load basic information needed for this page.
			const awardObs = self.awardSvc.CurrentAwardsSub;
			const curYearObs = self.settingsSvc.CurrentSchoolYearSubject;
            const curSchoolObs = self.settingsSvc.CurrentSchoolSubject;

            combineLatest(awardObs, curYearObs, curSchoolObs).pipe(
                takeUntil(self._subrelease),
                map(_pck => {
                    return {
                        Award: _pck[0],
                        CurYear: _pck[1],
                        CurSchool: _pck[2],
                    };
                })
            )
            .subscribe(packet => {
                self.AwardInfo = packet.Award;
				self.CurrentSchoolYear = packet.CurYear;
				self.CurrentSchool = packet.CurSchool;

                // Early exit if user is null.
                if (!self.User) {
                    return;
                }

				(self.AwardInfo && self.CurrentSchool ? self.appSvc.GetNominationsByWorkLocation(
					self.User.AuthToken,
					self.CurrentSchool.SchoolNumber,
					self.AwardInfo.AwardID,
					self.CurrentSchoolYear
				) : of<SchoolNominationInfo>(null).pipe(take(1))
				).subscribe(nominfo => {
					//put nominee and nomType together into a SchoolNomineeInfo
					self.Nominations = [];

					for (let n of nominfo.NomineeType) {
						let nInfo = new SchoolNomineeInfo();

						nInfo.NomineeInfo = nominfo.Nominees.find(x => x.NomineeTypeID === n.NomineeTypeID && x.GradeLevel === n.GradeLevel);

						switch (n.GradeLevel) {
							case "STUDENTELEM":
								n.GradeDisplay = "Elementary School Nominee";
								break;
							case "STUDENTMIDDLE":
								n.GradeDisplay = "Middle School Nominee";
								break;
							case "STUDENTHIGH":
								n.GradeDisplay = "High School Nominee";
								break;
							default:
								n.GradeDisplay = null;
						}

						nInfo.NomineeType = n;
						self.Nominations.push(nInfo);
					}

					self.ViewReady = true;
				},
					error => self.errSvc.HandleServiceError(error)
				);
            },
            error => self.errSvc.HandleServiceError(error)
            );
        },
        error => self.errSvc.HandleServiceError(error)
        );
    }
}
